import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { concat, flow, map, orderBy } from 'lodash/fp';
import { Box, Paper, Typography } from '@mui/material';
import Dropdown, { DropDownOptions } from 'components/@common/Inputs/Dropdown';
import { translate } from 'translations/translator';
import { AppStoreState } from '../../../types';
import { getClassroomStudents } from '../../../state/modules/entity/selectors/student-selectors';

type Props = {
  classroomId: number | null;
  studentId: number | null;
  onFilterChange: (filter) => void;
};

export type AssignmentFilters = {
  classroomId: number | null;
  studentId: number | null;
};

const getTranslation = (key, options?) => translate('assignmentTracker', key, options);

const Filters: React.FC<Props> = ({ classroomId, studentId, onFilterChange }) => {
  const classrooms = useSelector(({ entity }: AppStoreState) => entity.classrooms) ?? [];
  const students = useSelector(state => getClassroomStudents(state, { classroomId }));

  const classroomOptions = useMemo(
    () =>
      flow(
        orderBy('name', 'asc'),
        map((x: any) => ({ value: x.id, label: x.name }))
      )(classrooms) as DropDownOptions[],
    [classrooms]
  );

  const studentOptions = useMemo(
    () =>
      flow(
        orderBy(['lastName', 'firstName'], 'asc'),
        map((x: any) => ({ value: x.id, label: `${x.firstName} ${x.lastName}` })),
        concat([{ value: '', label: getTranslation('allOption') }])
      )(students) as DropDownOptions[],
    [students]
  );

  const handleClassroomChange = (_, value) => {
    onFilterChange({ ...(Number(value) && { classroomId: Number(value) }) });
  };

  const handleStudentChange = (_, value) => {
    onFilterChange({ classroomId, ...(Number(value) && { studentId: Number(value) }) });
  };

  return (
    <Box>
      <Paper>
        <Box px={5} display="flex" sx={{ paddingTop: '20px', paddingBottom: '0px' }}>
          <Box mr={4}>
            <Typography>{getTranslation('classroomFilter')}</Typography>
            <Dropdown
              displayEmpty
              name="classroomId"
              value={classroomId ?? ''}
              onChange={handleClassroomChange}
              options={classroomOptions}
              style={{ width: 300 }}
            />
          </Box>
          <Box>
            <Typography>{getTranslation('studentFilter')}</Typography>
            <Dropdown
              displayEmpty
              name="studentId"
              value={studentId ?? ''}
              onChange={handleStudentChange}
              options={studentOptions}
              style={{ width: 300 }}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Filters;
